<template>
  <header class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 relative z-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-20 gap-3">
        <!-- Logo -->
        <div class="flex items-center">
          <router-link to="/" class="flex items-center space-x-3 flex-shrink-0">
            <img 
              src="/teamio_new_logo_trans_1.png" 
              alt="Teamio" 
              class="h-8 w-auto sm:h-10"
            />
            <img 
              src="/teamio_logo_640x360_transparent.png" 
              alt="Teamio" 
              class="hidden md:block h-12 w-auto"
            />
          </router-link>
        </div>

        <!-- Navigation -->
        <nav class="hidden md:flex items-center space-x-8">
          <router-link 
            v-if="isAuthenticatedForHeader"
            to="/players" 
            class="px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
            :class="$route.path === '/players' 
              ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
              : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
          >
            Players
          </router-link>
          <router-link 
            v-if="isAuthenticatedForHeader"
            to="/teams" 
            class="px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
            :class="$route.path === '/teams' 
              ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
              : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
          >
            Teams
          </router-link>
          <router-link 
            v-if="isSystemAdminForHeader"
            to="/admin" 
            class="px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
            :class="$route.path.startsWith('/admin') 
              ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
              : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
          >
            Admin
          </router-link>
        </nav>


        <!-- User Menu -->
        <div class="hidden md:flex items-center space-x-4">
          <template v-if="isAuthenticatedForHeader">
            <router-link 
              to="/dashboard" 
              class="px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
              :class="$route.path === '/dashboard' 
                ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
                : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
            >
              Dashboard
            </router-link>
            <router-link 
              to="/profile" 
              class="px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
              :class="$route.path === '/profile' 
                ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
                : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
            >
              Profile
            </router-link>
            <button 
              @click="handleSignOut"
              class="bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] hover:from-[#4C1A5D] hover:to-[#1A2B5D] text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Sign Out
            </button>
          </template>
          <template v-else>
            <router-link 
              to="/login" 
              class="px-3 py-2 rounded-md text-sm font-medium transition-all duration-200"
              :class="$route.path === '/login' 
                ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
                : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
            >
              Login
            </router-link>
            <router-link 
              to="/register" 
              class="px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              :class="$route.path === '/register' 
                ? 'bg-gradient-to-r from-[#4C1A5D] to-[#1A2B5D] text-gray-200 focus:text-gray-200 active:text-gray-200' 
                : 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] hover:from-[#4C1A5D] hover:to-[#1A2B5D] text-white focus:text-white active:text-white'"
            >
              Register
            </router-link>
          </template>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden">
          <button 
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="text-[#5C2A6D] hover:text-[#2A3B6D] p-2 rounded-md transition-colors duration-200"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile menu -->
      <div v-if="mobileMenuOpen" class="md:hidden">
        <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200 bg-gradient-to-r from-[#5C2A6D]/5 to-[#2A3B6D]/5">
          <router-link 
            v-if="isAuthenticatedForHeader"
            to="/players" 
            class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200"
            :class="$route.path === '/players' 
              ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
              : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
            @click="mobileMenuOpen = false"
          >
            Players
          </router-link>
          <router-link 
            v-if="isAuthenticatedForHeader"
            to="/teams" 
            class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200"
            :class="$route.path === '/teams' 
              ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
              : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
            @click="mobileMenuOpen = false"
          >
            Teams
          </router-link>
          <router-link 
            v-if="isSystemAdminForHeader"
            to="/admin" 
            class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200"
            :class="$route.path.startsWith('/admin') 
              ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
              : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
            @click="mobileMenuOpen = false"
          >
            Admin
          </router-link>
          
          <template v-if="isAuthenticatedForHeader">
            <router-link 
              to="/dashboard" 
              class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200"
              :class="$route.path === '/dashboard' 
                ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
                : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
              @click="mobileMenuOpen = false"
            >
              Dashboard
            </router-link>
            <router-link 
              to="/profile" 
              class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200"
              :class="$route.path === '/profile' 
                ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
                : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
              @click="mobileMenuOpen = false"
            >
              Profile
            </router-link>
            <button 
              @click="handleSignOut"
              class="block w-full text-left bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] hover:from-[#4C1A5D] hover:to-[#1A2B5D] text-white px-3 py-2 rounded-md text-base font-medium transition-all duration-200 shadow-md"
            >
              Sign Out
            </button>
          </template>
          <template v-else>
            <router-link 
              to="/login" 
              class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200"
              :class="$route.path === '/login' 
                ? 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] text-gray-200 shadow-md focus:text-gray-200 active:text-gray-200' 
                : 'text-[#5C2A6D] hover:text-gray-700 hover:bg-gradient-to-r hover:from-[#5C2A6D]/10 hover:to-[#2A3B6D]/10 focus:text-gray-700 active:text-gray-700'"
              @click="mobileMenuOpen = false"
            >
              Login
            </router-link>
            <router-link 
              to="/register" 
              class="block px-3 py-2 rounded-md text-base font-medium transition-all duration-200 shadow-md"
              :class="$route.path === '/register' 
                ? 'bg-gradient-to-r from-[#4C1A5D] to-[#1A2B5D] text-gray-200 focus:text-gray-200 active:text-gray-200' 
                : 'bg-gradient-to-r from-[#5C2A6D] to-[#2A3B6D] hover:from-[#4C1A5D] hover:to-[#1A2B5D] text-white focus:text-white active:text-white'"
              @click="mobileMenuOpen = false"
            >
              Register
            </router-link>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { supabase } from '@/lib/supabase'

const $route = useRoute()
const authStore = useAuthStore()
const mobileMenuOpen = ref(false)

// More resilient authentication check for header
const isAuthenticatedForHeader = computed(() => {
  // If we have user data, we're authenticated regardless of session state
  if (authStore.user && authStore.user.profile) {
    return true
  }
  
  // If we have a valid session, we're authenticated
  if (authStore.session && 
      (!authStore.session.expires_at || new Date(authStore.session.expires_at) > new Date())) {
    return true
  }
  
  // Fallback: Check if we have any indication the user was logged in
  // This prevents menu from disappearing due to temporary state issues
  const hasAnyUserData = authStore.user || authStore.session
  if (hasAnyUserData) {
    return true
  }
  
  // Last resort: Check localStorage to see if user was logged in
  const wasLoggedIn = localStorage.getItem('teamio_user_was_logged_in') === 'true'
  if (wasLoggedIn) {
    return true
  }
  
  return false
})

// More resilient system admin check for header
const isSystemAdminForHeader = computed(() => {
  // If we have user data with system_admin role, show admin button
  if (authStore.user?.profile?.role === 'system_admin') {
    return true
  }
  
  // If we have a valid session with system_admin role, show admin button
  if (authStore.session?.user?.app_metadata?.user_role === 'system_admin') {
    return true
  }
  
  // Fallback: Check if user was system admin and we have any user data
  const hasAnyUserData = authStore.user || authStore.session
  if (hasAnyUserData) {
    // Check JWT claims for system_admin role
    const jwtRole = authStore.session?.user?.app_metadata?.user_role
    if (jwtRole === 'system_admin') {
      return true
    }
  }
  
  return false
})

// Debug logging for admin link visibility
// Enhanced debug logging

// Watch for authentication state changes and try to restore if needed
watch([() => isAuthenticatedForHeader, () => authStore.user, () => authStore.session], 
  async ([isAuth, user, session]) => {
    console.log('🔍 AppHeader - Auth state changed:', { isAuth, hasUser: !!user, hasSession: !!session })
    
    // If we have user but no session, try to restore
    if (user && !session) {
      console.log('🔄 AppHeader - User exists but no session, attempting restore...')
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession()
        if (currentSession) {
          console.log('✅ AppHeader - Found session, updating...')
          authStore.session = currentSession
        }
      } catch (error) {
        console.error('AppHeader - Failed to get session:', error)
      }
    }
  }, 
  { immediate: false }
)

// Watch for auth changes and re-debug
watch(() => authStore.isSystemAdmin, (newValue, oldValue) => {
  console.log('🔄 AppHeader - isSystemAdmin changed:', { from: oldValue, to: newValue })
})

watch(() => isSystemAdminForHeader.value, (newValue, oldValue) => {
  console.log('🔄 AppHeader - isSystemAdminForHeader changed:', { from: oldValue, to: newValue })
})

watch(() => authStore.user?.profile?.role, (newValue, oldValue) => {
  console.log('🔄 AppHeader - user role changed:', { from: oldValue, to: newValue })
})

const handleSignOut = () => {
  console.log('🚪 Starting sign out process...')
  
  // Clear local state immediately
  authStore.clearUserSession()
  localStorage.removeItem('teamio_user_was_logged_in')
  console.log('✅ Local state cleared')
  
  // Sign out from Supabase in background (don't wait)
  authStore.signOut().catch(error => {
    console.error('⚠️ Sign out error (will redirect anyway):', error)
  })
  
  // Force IMMEDIATE hard redirect with full URL
  console.log('🔄 Force IMMEDIATE redirect to login...')
  
  // Use the origin + path for absolute certainty
  const loginUrl = window.location.origin + '/login'
  console.log('🔄 Redirecting to:', loginUrl)
  
  // Use href for maximum compatibility - this WILL reload the page
  window.location.href = loginUrl
}
</script>